import { supabase } from '@/lib/supabase/client';
import type { z } from 'zod';
import {
  adminApiErrorEnvelopeSchema,
  classifyProofApiError,
  type ProofApiErrorKind,
} from '@/lib/proof-extraction/operator-contract';
import { resolveProofApiUrl } from './proof-api-origin';

export class ProofApiClientError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly kind: ProofApiErrorKind,
    readonly retryable: boolean,
    readonly requestId: string | null,
  ) {
    super(message);
  }
}

async function accessToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.access_token) {
    throw new ProofApiClientError(
      'Your admin session expired. Sign in again.',
      'unauthorized',
      'auth_expired',
      false,
      null,
    );
  }
  return data.session.access_token;
}

export async function proofApiFetch<T>(
  path: string,
  responseSchema: z.ZodType<T>,
  init?: RequestInit,
): Promise<T> {
  let requestUrl: string;
  try {
    requestUrl = resolveProofApiUrl(path, import.meta.env.VITE_API_URL || '', window.location.origin);
  } catch {
    throw new ProofApiClientError(
      'The proof service configuration is invalid.',
      'proof_api_origin_invalid',
      'terminal',
      false,
      null,
    );
  }
  const token = await accessToken();
  let response: Response;
  try {
    response = await fetch(requestUrl, {
      ...init,
      cache: 'no-store',
      headers: {
        Authorization: `Bearer ${token}`,
        ...init?.headers,
      },
    });
  } catch {
    throw new ProofApiClientError(
      'The proof service could not be reached. Try again.',
      'proof_network_failed',
      'retryable',
      true,
      null,
    );
  }

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const parsed = adminApiErrorEnvelopeSchema.safeParse(payload);
    if (!parsed.success) {
      throw new ProofApiClientError(
        'The proof service returned an invalid response.',
        'proof_response_invalid',
        response.status >= 500 ? 'retryable' : 'terminal',
        response.status >= 500,
        response.headers.get('x-request-id'),
      );
    }
    const detail = parsed.data.error;
    throw new ProofApiClientError(
      detail.message,
      detail.code,
      classifyProofApiError(detail.code, detail.retryable),
      detail.retryable,
      detail.requestId,
    );
  }
  const parsed = responseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new ProofApiClientError(
      'The proof service returned an invalid response.',
      'proof_response_invalid',
      'terminal',
      false,
      response.headers.get('x-request-id'),
    );
  }
  return parsed.data;
}
